import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { TIDS } from "@/constants/testIds";
import { Plus, ExternalLink, Copy, Trash2, Sparkles as SparklesIcon, LayoutPanelLeft, Globe2, CopyPlus, CheckCircle2, Circle, Code2, Shield, UserCog, FileCode2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PasteHtmlModal from "@/components/PasteHtmlModal";

const FOUNDER_EMAILS = new Set(["admin@videoepreneur.com", "admin@videoprenuer.com"]);

export default function LandingPages() {
  const { user, refreshMe } = useAuth();
  const isFounder = user && FOUNDER_EMAILS.has((user.email || "").toLowerCase());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dupBusy, setDupBusy] = useState(null);
  const [pubBusy, setPubBusy] = useState(null);
  const [domains, setDomains] = useState([]);
  const [openDomainFor, setOpenDomainFor] = useState(null);
  const [domainBusy, setDomainBusy] = useState(false);
  // Founder-only view of every page in every workspace
  const [showAllWs, setShowAllWs] = useState(false);
  const [allWsRows, setAllWsRows] = useState([]);
  const [allWsLoading, setAllWsLoading] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: pages }, domainsResp] = await Promise.all([
      api.get("/landing-pages"),
      api.get("/domains").catch(() => ({ data: [] })),
    ]);
    setRows(pages);
    setDomains((domainsResp.data || []).filter((d) => d.status === "verified"));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Load all workspaces list when founder toggles it on
  useEffect(() => {
    if (!showAllWs || !isFounder) return;
    (async () => {
      setAllWsLoading(true);
      try {
        const { data } = await api.get("/admin/landing-pages");
        setAllWsRows(data.pages || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
      } finally { setAllWsLoading(false); }
    })();
  }, [showAllWs, isFounder]);

  const impersonate = async (userId, userEmail) => {
    if (!userId) { toast.error("No owner found for this workspace"); return; }
    if (!window.confirm(`Impersonate ${userEmail}? You'll be redirected to view the app as them.`)) return;
    try {
      await api.post(`/admin/impersonate/${userId}`);
      await refreshMe();
      toast.success(`Now viewing as ${userEmail}`);
      window.location.assign("/app/pages");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/p/${slug}`);
    toast.success("Landing page link copied");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this landing page?")) return;
    try { await api.delete(`/landing-pages/${id}`); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const duplicate = async (id) => {
    setDupBusy(id);
    try {
      const { data } = await api.post(`/landing-pages/${id}/duplicate`);
      setRows((r) => [data, ...r]);
      toast.success(`Duplicated as "${data.title}"`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setDupBusy(null); }
  };

  const togglePublish = async (p) => {
    setPubBusy(p.id);
    try {
      const { data } = await api.patch(`/landing-pages/${p.id}`, { published: !p.published });
      setRows((r) => r.map((x) => (x.id === p.id ? data : x)));
      if (data.published) {
        toast.success(`Published! Live at /p/${data.slug}`);
      } else {
        toast.success("Unpublished — page is now a draft");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setPubBusy(null); }
  };

  const linkDomain = async (domainId, pageId) => {
    setDomainBusy(true);
    try {
      await api.patch(`/domains/${domainId}/primary-page`, { primary_page_id: pageId || null });
      // Update the local domain list so subsequent picks reflect the new link
      setDomains((prev) => prev.map((d) => (d.id === domainId ? { ...d, primary_page_id: pageId || null } : d)));
      const dom = domains.find((d) => d.id === domainId);
      toast.success(pageId ? `Linked to ${dom?.domain || "domain"}` : "Cleared domain link");
      setOpenDomainFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setDomainBusy(false); }
  };

  // Close the domain popover on click-outside / Escape
  useEffect(() => {
    if (!openDomainFor) return;
    const onDoc = (e) => { if (!e.target.closest?.("[data-domain-pop]")) setOpenDomainFor(null); };
    const onKey = (e) => { if (e.key === "Escape") setOpenDomainFor(null); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("keydown", onKey); };
  }, [openDomainFor]);

  return (
    <div className="space-y-6 vp-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="vp-label">Landing pages</div>
          <h1 className="font-display text-3xl font-bold mt-1">Your brand landing pages</h1>
          <p className="text-[#4A4D57] mt-2 text-sm max-w-xl">Pick a template, drop your content, publish on your own domain. Or let AI draft the whole thing in ≤ 40 seconds.</p>
        </div>
        <div className="flex items-center gap-2">
          {isFounder && (
            <button
              onClick={() => setShowAllWs((v) => !v)}
              data-testid="founder-toggle-all-ws"
              className={"inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg border " +
                (showAllWs ? "bg-[#0B0D17] text-white border-[#0B0D17]" : "bg-white text-[#0B0D17] border-[#E4E5E9] hover:border-[#0B0D17]")}
              title="Founder-only — see every landing page across every workspace"
            >
              <Shield className="w-4 h-4" /> {showAllWs ? "My pages" : "All workspaces"}
            </button>
          )}
          <button
            onClick={async () => {
              try {
                const { data } = await api.post("/landing-pages", {
                  template_id: "blank",
                  title: "Code page",
                  kind: "landing",
                });
                // seed a single full-height custom_code section
                await api.patch(`/landing-pages/${data.id}`, {
                  sections: [{
                    id: "cc" + Math.random().toString(36).slice(2, 8),
                    kind: "custom_code",
                    html: '<div class="wrap">\n  <h1>Your code page</h1>\n  <p>Write HTML, CSS and JS directly — this whole page is one sandboxed iframe.</p>\n</div>',
                    css: 'body{font-family:"Manrope",sans-serif;margin:0}\n.wrap{min-height:100vh;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;padding:40px 24px;background:linear-gradient(135deg,#0B5CFF,#7C3AED);color:#fff}\n.wrap h1{font-size:64px;margin:0 0 16px}\n.wrap p{font-size:20px;opacity:.9;max-width:520px}',
                    js: '// Full-page code mode — write anything you want!\nconsole.log("Code page loaded");',
                    height: 720,
                  }],
                });
                window.location.href = `/app/pages/${data.id}/edit`;
              } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
            }}
            data-testid="new-code-page-btn"
            className="vp-btn-ghost inline-flex items-center gap-2 text-sm"
            title="Start with a blank HTML/CSS/JS canvas"
          >
            <Code2 className="w-4 h-4" /> Code page
          </button>
          <button
            onClick={() => setPasteOpen(true)}
            data-testid="paste-html-btn"
            className="vp-btn-ghost inline-flex items-center gap-2 text-sm border-[#0B5CFF] text-[#0B5CFF] hover:bg-[#0B5CFF]/5"
            title="Paste a full HTML template and turn it into an editable page"
          >
            <FileCode2 className="w-4 h-4" /> Paste HTML
          </button>
          <Link to="/app/pages/new" data-testid={TIDS.pageNewBtn} className="vp-btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New page
          </Link>
        </div>
      </div>

      {pasteOpen && <PasteHtmlModal onClose={() => setPasteOpen(false)} />}

      {isFounder && showAllWs && (
        <div className="vp-card p-4" data-testid="all-workspaces-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#B00020]" />
              <div className="font-display font-bold">All landing pages · every workspace</div>
              <span className="vp-chip text-xs">{allWsRows.length} total</span>
            </div>
            <div className="text-xs text-[#747680]">Founder view — impersonate to edit or view.</div>
          </div>
          {allWsLoading ? (
            <div className="text-[#4A4D57] text-sm">Loading…</div>
          ) : allWsRows.length === 0 ? (
            <div className="text-[#4A4D57] text-sm">No pages found across workspaces.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[#747680] text-xs uppercase tracking-widest">
                  <tr className="text-left">
                    <th className="py-2 pr-2">Title / Slug</th>
                    <th className="py-2 pr-2">Owner</th>
                    <th className="py-2 pr-2">Status</th>
                    <th className="py-2 pr-2">Sections</th>
                    <th className="py-2 pr-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allWsRows.map((r) => (
                    <tr key={r.id} className="border-t border-[#EEF0F4]">
                      <td className="py-2 pr-2">
                        <div className="font-semibold text-[#0B0D17]">{r.title}</div>
                        <div className="text-xs text-[#747680]">/p/{r.slug}</div>
                      </td>
                      <td className="py-2 pr-2">
                        <div className="text-[#0B0D17]">{r.owner?.name || "—"}</div>
                        <div className="text-xs text-[#747680]">{r.owner?.email || `ws:${r.workspace_id}`}</div>
                      </td>
                      <td className="py-2 pr-2">
                        <span className={"vp-chip text-xs " + (r.published ? "vp-chip-accent" : "")}>
                          {r.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="py-2 pr-2 text-[#4A4D57]">{r.sections_count}</td>
                      <td className="py-2 pr-2">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {r.published && (
                            <a href={`/p/${r.slug}`} target="_blank" rel="noreferrer"
                              className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1 px-2">
                              <ExternalLink className="w-3.5 h-3.5" /> View
                            </a>
                          )}
                          <button
                            onClick={() => impersonate(r.owner?.id, r.owner?.email)}
                            disabled={!r.owner?.id}
                            data-testid={`impersonate-owner-${r.id}`}
                            className="text-xs inline-flex items-center gap-1 py-1 px-2 rounded-lg border border-[#E4E5E9] hover:border-[#0B0D17] text-[#0B0D17] disabled:opacity-40"
                            title={r.owner?.id ? "Impersonate owner to edit" : "No owner account resolved"}
                          >
                            <UserCog className="w-3.5 h-3.5" /> Impersonate & edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading ? <div className="text-[#4A4D57]">Loading…</div> : rows.length === 0 ? (
        <div className="vp-card p-10 text-center">
          <div className="w-12 h-12 rounded-2xl bg-[#0B5CFF]/10 flex items-center justify-center mx-auto mb-4">
            <LayoutPanelLeft className="w-6 h-6 text-[#0B5CFF]" strokeWidth={1.5} />
          </div>
          <div className="font-display font-bold text-xl">No landing pages yet</div>
          <p className="text-[#4A4D57] mt-2 text-sm max-w-md mx-auto">Create your first page from one of six templates or start blank.</p>
          <Link to="/app/pages/new" className="vp-btn-primary mt-5 inline-block">Create your first page</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((p) => (
            <div key={p.id} className="vp-card vp-card-hover p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <Link to={`/app/pages/${p.id}/edit`} className="font-display font-bold text-lg hover:text-[#0B5CFF] leading-snug">{p.title}</Link>
                <span className={"vp-chip " + (p.published ? "vp-chip-accent" : "")}>{p.published ? "Published" : "Draft"}</span>
              </div>
              <div className="text-xs text-[#747680] mt-2">Template · {p.template_id} · {p.sections?.length || 0} sections</div>
              <div className="mt-auto pt-4 flex gap-2 flex-wrap">
                <Link to={`/app/pages/${p.id}/edit`} className="vp-btn-primary text-xs inline-flex items-center gap-1 py-1.5 px-3">Edit</Link>
                <button
                  onClick={() => togglePublish(p)}
                  disabled={pubBusy === p.id}
                  className={"text-xs inline-flex items-center gap-1 py-1.5 px-3 rounded-lg font-semibold border transition-colors " +
                    (p.published
                      ? "border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
                      : "border-[#F59E0B] bg-[#F59E0B] text-white hover:bg-[#D97706]")}
                  data-testid={`publish-toggle-${p.id}`}
                  title={p.published ? "Currently public. Click to unpublish." : "Make it public"}
                >
                  {p.published ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                  {pubBusy === p.id ? "…" : p.published ? "Live" : "Publish"}
                </button>
                {p.published && domains.length > 0 && (
                  <div className="relative" data-domain-pop>
                    <button
                      onClick={() => setOpenDomainFor(openDomainFor === p.id ? null : p.id)}
                      className="text-xs inline-flex items-center gap-1 py-1.5 px-3 rounded-lg border border-[#E4E5E9] hover:border-[#0B5CFF] text-[#0B0D17] font-semibold"
                      data-testid={`link-domain-${p.id}`}
                      title="Publish on your custom domain"
                    >
                      <Globe2 className="w-3.5 h-3.5" />
                      {(() => {
                        const linked = domains.find((d) => d.primary_page_id === p.id);
                        return linked ? linked.domain : "Domain";
                      })()}
                      <span className="ml-1 text-[#747680]">▾</span>
                    </button>
                    {openDomainFor === p.id && (
                      <div className="absolute right-0 top-full mt-1 w-64 rounded-xl bg-white border border-[#E4E5E9] shadow-xl z-30 p-2">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#747680] px-3 pt-1 pb-2">Serve on…</div>
                        {domains.map((d) => {
                          const isLinked = d.primary_page_id === p.id;
                          const isLinkedToOther = d.primary_page_id && d.primary_page_id !== p.id;
                          return (
                            <button
                              key={d.id}
                              onClick={() => linkDomain(d.id, p.id)}
                              disabled={domainBusy || isLinkedToOther}
                              className={"w-full text-left px-3 py-2 rounded-lg flex items-center justify-between gap-2 " +
                                (isLinked ? "bg-[#10B981]/10 text-[#10B981]" : isLinkedToOther ? "opacity-40 cursor-not-allowed" : "hover:bg-[#F5F5F7] text-[#0B0D17]")}
                              data-testid={`link-domain-option-${d.id}`}
                              title={isLinkedToOther ? "This domain is linked to another page — unlink it first." : ""}
                            >
                              <span className="text-sm font-mono truncate">{d.domain}</span>
                              {isLinked ? <CheckCircle2 className="w-4 h-4" /> : null}
                            </button>
                          );
                        })}
                        <div className="border-t border-[#E4E5E9] my-1"></div>
                        {domains.find((d) => d.primary_page_id === p.id) && (
                          <button
                            onClick={() => linkDomain(domains.find((d) => d.primary_page_id === p.id).id, null)}
                            className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F5F5F7] text-xs text-[#B00020]"
                          >Unlink from domain</button>
                        )}
                        <Link
                          to="/app/domains"
                          className="block w-full text-left px-3 py-2 rounded-lg hover:bg-[#F5F5F7] text-xs text-[#0B5CFF]"
                        >Manage domains →</Link>
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => copyLink(p.slug)} className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3">
                  <Copy className="w-3.5 h-3.5" /> Link
                </button>
                <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  onClick={() => duplicate(p.id)}
                  disabled={dupBusy === p.id}
                  data-testid={`duplicate-page-${p.id}`}
                  title="Duplicate page"
                  className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3 disabled:opacity-60"
                >
                  <CopyPlus className="w-3.5 h-3.5" /> {dupBusy === p.id ? "…" : "Duplicate"}
                </button>
                <button onClick={() => remove(p.id)} className="ml-auto p-1.5 text-[#4A4D57] hover:text-[#B00020] rounded-md hover:bg-[#FDE8EA]">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
